<template>
    <main class="ticket-page">
        <section class="ticket-card" v-if="!disabled && request.supportRequest">
            <div class="ticket-header">
                <div class="header-right">
                    <h5 class="ticket-title">جزییات تیکت #{{ id }}</h5>
                    <b-badge :variant="request.supportRequest.cancelled_at ? 'secondary' : 'success'"
                        class="status-badge">
                        {{ request.supportRequest.cancelled_at ? 'لغو شده' : 'فعال' }}
                    </b-badge>
                </div>
                <div class="header-actions">
                    <span class="ticket-date">{{ request.supportRequest.created_at | persianDate }}</span>
                    <b-button size="sm" variant="outline-danger" class="action-btn" :disabled="actionLoading"
                        @click="deleteTicket">حذف تیکت</b-button>
                </div>
            </div>

            <div class="info-section" v-if="request.supportRequest.customer">
                <span class="section-label">مشتری:</span>
                <span class="section-value">
                    {{ request.supportRequest.customer.first_name }} {{ request.supportRequest.customer.last_name }}
                    <span class="customer-mobile">({{ request.supportRequest.customer.mobile }})</span>
                </span>
            </div>

            <div class="info-section">
                <span class="section-label">نوع:</span>
                <b-badge :variant="request.supportRequest.type === 'refund' ? 'danger' : 'info'" class="type-badge">
                    {{ request.supportRequest.type === 'refund' ? 'مرجوعی' : 'پشتیبانی' }}
                </b-badge>
            </div>
            
            <div class="info-section">
                <span class="section-label">وضعیت:</span>
                <div class="status-inline-wrap">
                    <span class="status-pill"
                        :style="{ background: statusColor + '18', color: statusColor, border: '1px solid ' + statusColor + '40' }">
                        <span class="status-dot-sm" :style="{ background: statusColor }"></span>
                        {{ statusLabel }}
                    </span>
                    <button class="btn btn-warning btn-sm btn-icon" @click="showStatusMenu = !showStatusMenu"
                        :disabled="actionLoading" title="تغییر وضعیت">
                        <i class="fa fa-edit"></i>
                    </button>
                    <div class="status-menu" v-if="showStatusMenu">
                        <button v-for="s in statusOptions" :key="s.value" class="status-menu-item"
                            :class="{ active: request.supportRequest.status === s.value }"
                            @click="changeStatus(s.value)">
                            <span class="status-dot" :style="{ background: s.color }"></span>
                            {{ s.label }}
                        </button>
                    </div>
                </div>
            </div>
           
            <div class="info-section">
                <span class="section-label">علت {{ request.supportRequest.type === 'refund' ? 'مرجوعی' : 'پشتیبانی' }}:</span>
                <span> {{request.supportRequest.type === 'refund' ? traslateRefundTypes(request.supportRequest.refund_type) : traslateSupportTypes(request.supportRequest.support_type) }}</span>
            </div>
            
            <div class="desc-section" v-if="request.supportRequest.description">
                <span class="section-label">توضیحات:</span>
                <p class="desc-text">{{ request.supportRequest.description }}</p>
            </div>

            <div class="image-section" v-if="request.supportRequest.image">
                <span class="section-label">تصویر پیوست:</span>
                <img :src="request.supportRequest.image" alt="تصویر تیکت" class="ticket-image" />
            </div>

            <div class="items-section"
                v-if="request.supportRequest.type === 'refund' && request.supportRequest.order_items && request.supportRequest.order_items.length">
                <div class="items-header">
                    <span class="section-label">آیتم‌های مرجوعی:</span>
                    <router-link :to="`/admin/order/${request.supportRequest.order_id}`" class="order-detail-btn">
                        جزییات سفارش ←
                    </router-link>
                </div>
                <ul class="items-list">
                    <li v-for="(item, index) in request.supportRequest.order_items" :key="index" class="item-row">
                        <span class="item-index">{{ index + 1 }}.</span>
                        <span class="item-name">{{ item.variety_name }}</span>
                    </li>
                </ul>
            </div>
        </section>

        <div v-if="disabled" class="loading-state">
            <b-spinner label="در حال بارگذاری..." />
            <span>در حال بارگذاری...</span>
        </div>

        <!-- تاریخچه پیام‌ها -->
        <section class="chat-section" v-if="!disabled && request.supportRequest">
            <div class="chat-header">
                <span class="chat-title">تاریخچه گفتگو</span>
                <span class="chat-count" v-if="request.supportRequest.answers && request.supportRequest.answers.length">
                    {{ request.supportRequest.answers.length }} پیام
                </span>
            </div>

            <div class="chat-body" ref="chatBody">
                <template v-if="request.supportRequest.answers && request.supportRequest.answers.length">
                    <div v-for="answer in request.supportRequest.answers" :key="answer.id" class="chat-messages"
                        :class="answer.admin_id ? 'admin-message' : 'customer-message'">
                        <div class="message-avatar" :class="answer.admin_id ? 'admin-avatar' : 'customer-avatar'">
                            <span>{{ answer.admin_id ? 'پ' : getInitials(request.supportRequest.customer) }}</span>
                        </div>
                        <div class="message-content" :class="answer.admin_id ? 'align-left' : 'align-right'">
                            <div class="message-meta" :class="answer.admin_id ? 'meta-left' : 'meta-right'">
                                <span class="message-sender">{{ answer.admin_id ? 'پشتیبان سایت' : 'مشتری' }}</span>
                                <span class="message-time">{{ answer.created_at | persianDate }}</span>
                            </div>
                            <div class="message-bubble" :class="answer.admin_id ? 'admin-bubble' : 'customer-bubble'">
                                <p v-if="answer.description" class="bubble-text">{{ answer.description }}</p>
                                <img v-if="answer.image" :src="answer.image" class="bubble-image"
                                    @click="openImage(answer.image)" />
                            </div>
                        </div>
                    </div>
                </template>

                <div v-else class="chat-empty">
                    <span class="chat-empty-icon">💬</span>
                    <p>هنوز پاسخی ثبت نشده</p>
                </div>
            </div>
        </section>

        <div class="reply-box">
            <div class="reply-inner">

                <div class="image-preview-wrap" v-if="replyImage">
                    <div class="image-preview-inner">
                        <img :src="replyImage" class="image-preview" />
                        <button class="remove-image-btn" @click="removeImage" title="حذف تصویر">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.8"
                                    stroke-linecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div class="reply-input-row">
                    <label class="upload-label" :class="{ 'has-image': replyImage }" title="افزودن تصویر">
                        <input type="file" accept="image/*" class="upload-input" ref="imageInput"
                            @change="onImageSelect" />
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="3" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                        </svg>
                    </label>

                    <textarea v-model="replyText" class="reply-textarea" placeholder="پاسخ خود را بنویسید..." rows="2"
                        @keydown.ctrl.enter="sendReply"></textarea>

                    <b-button variant="primary" class="send-btn"
                        :disabled="(!replyText.trim() && !replyImage) || sending" @click="sendReply">
                        <b-spinner small v-if="sending" />
                        <span v-else>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                style="transform: rotate(180deg)">
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                        </span>
                    </b-button>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import configProvider from "@@/core/configProvider";
import mixins from "@@/core/mixins/mixins";
import { BTooltip, BButton, BCard, BTable, BBadge, BSpinner } from "bootstrap-vue";

export default {
    name: "supportTicketDetail",
    mixins: [mixins],
    components: {
        BTooltip, BButton, BCard, BTable, BBadge, BSpinner
    },
    configProvider,

    data() {
        return {
            id: this.$route.params.id,
            request: {},
            disabled: true,
            replyText: "",
            sending: false,
            replyImage: null,
            replyImageFile: null,
            actionLoading: false,
            showStatusMenu: false,
            statusOptions: [
                { value: 'new', label: 'جدید', color: '#6c757d' },
                { value: 'pending', label: 'در انتظار', color: '#fd7e14' },
                { value: 'answered', label: 'پاسخ داده شده', color: '#0d6efd' },
                { value: 'completed', label: 'تکمیل شده', color: '#198754' },
            ],
            refundTypes: [
                { title: "سلیقه ای", key: "personal_preference", id: 1 },
                { title: "مغایرت رنگ", key: "color_mismatch", id: 2 },
                { title: "مغایرت کالا ( کالای دیگر)", key: "item_mismatch", id: 3 },
                { title: "تعویض سایز", key: "size_exchange", id: 4 },
                { title: "محصول ناسالم", key: "damaged_product", id: 5 },
                { title: "سایر", key: "other", id: 6 },
            ],
            supportTypes: [
                { title: "وجه از حساب کسر شده اما سفارش تایید نشده",key: "deducted_money_but_order_not_confirmed",id: 1 },
                { title: "کنسل کردن سفارش", key: "cancel_order", id: 2 },
                { title: "پیگیری ارسال سفارش", key: "order_shipping_follow_up", id: 3 },
                { title: "تغییر ادرس", key: "change_address", id: 4 },
                { title: "اشتباه در انتخاب کالا", key: "wrong_item_selected", id: 5 },
                { title: "عدم اتصال به درگاه بانکی",key: "bank_gateway_connection_issue", id: 6 },
                { title: "عدم حذف ایتم از سبد خرید", key: "item_not_removed_from_cart", id: 7 },
                { title: "عدم اعمال کدتخفیف", key: "discount_code_not_applied", id: 8 },
                { title: "سایر", key: "other", id: 9 },
            ],
        };
    },
    mounted() {
        this.loadData();
    },
    computed: {
        statusLabel() {
            const map = { new: 'جدید', pending: 'در انتظار', answered: 'پاسخ داده شده', completed: 'تکمیل شده' };
            return map[this.request.supportRequest?.status] || this.request.supportRequest?.status || '—';
        },
        statusColor() {
            const map = { new: '#6c757d', pending: '#fd7e14', answered: '#0d6efd', completed: '#198754' };
            return map[this.request.supportRequest?.status] || '#6c757d';
        },
    },
    methods: {
        traslateSupportTypes(type) {
            if (!type) return null;
            let res = this.supportTypes.find((item) => item.key == type);
            if (res) {
                return res.title;
            } else {
                return null;
            }
        },
        traslateRefundTypes(type) {
            if (!type) return null;
            let res = this.refundTypes.find((item) => item.key == type);
            if (res) {
                return res.title;
            } else {
                return null;
            }
        },
        async loadData() {
            this.disabled = true;
            try {
                const response = await this.$axios.get(`admin/support_requests/${this.id}`);
                this.request = response.data.data;
                this.$nextTick(() => this.scrollToBottom());
            } catch (e) {
                console.error("خطا در دریافت تیکت", e);
            } finally {
                this.disabled = false;
            }
        },
        async sendReply() {
            if (!this.replyText.trim() && !this.replyImageFile) return;
            this.sending = true;
            try {
                const formData = new FormData();
                if (this.replyText.trim()) formData.append('description', this.replyText);
                if (this.replyImageFile) formData.append('image', this.replyImageFile);

                await this.$axios.post(`admin/support_requests/${this.id}/reply`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                this.$root.$emit("toast", { message: "پاسخ با موفقیت ارسال شد", variant: "success" });
                this.replyText = "";
                this.replyImage = null;
                this.replyImageFile = null;
                if (this.$refs.imageInput) this.$refs.imageInput.value = '';
                await this.loadData();
            } catch (e) {
                console.error("خطا در ارسال پاسخ", e);
                this.$root.$emit("toast", { message: "خطا در ارسال پاسخ", variant: "danger" });
            } finally {
                this.sending = false;
            }
        },
        onImageSelect(e) {
            const file = e.target.files[0];
            if (!file) return;
            this.replyImageFile = file;
            const reader = new FileReader();
            reader.onload = (ev) => {
                this.replyImage = ev.target.result;
            };
            reader.readAsDataURL(file);
        },
        removeImage() {
            this.replyImage = null;
            this.replyImageFile = null;
            if (this.$refs.imageInput) this.$refs.imageInput.value = '';
        },
        openImage(src) {
            window.open(src, '_blank');
        },
        async changeStatus(status) {
            this.showStatusMenu = false;
            this.actionLoading = true;
            try {
                await this.$axios.post(`admin/support_requests/${this.id}/status`, { status });
                this.$root.$emit('toast', { message: 'وضعیت تیکت با موفقیت تغییر کرد', variant: 'success' });
                await this.loadData();
            } catch (e) {
                this.$root.$emit('toast', { message: 'خطا در تغییر وضعیت', variant: 'danger' });
            } finally {
                this.actionLoading = false;
            }
        },
        async closeTicket() {
            if (!confirm('آیا از بستن این تیکت اطمینان دارید؟')) return;
            this.actionLoading = true;
            try {
                await this.$axios.post(`admin/support_requests/${this.id}/close`);
                this.$root.$emit('toast', { message: 'تیکت با موفقیت بسته شد', variant: 'success' });
                await this.loadData();
            } catch (e) {
                this.$root.$emit('toast', { message: 'خطا در بستن تیکت', variant: 'danger' });
            } finally {
                this.actionLoading = false;
            }
        },
        async reopenTicket() {
            if (!confirm('آیا از باز کردن مجدد این تیکت اطمینان دارید؟')) return;
            this.actionLoading = true;
            try {
                await this.$axios.post(`admin/support_requests/${this.id}/reopen`);
                this.$root.$emit('toast', { message: 'تیکت با موفقیت باز شد', variant: 'success' });
                await this.loadData();
            } catch (e) {
                this.$root.$emit('toast', { message: 'خطا در باز کردن تیکت', variant: 'danger' });
            } finally {
                this.actionLoading = false;
            }
        },
        async deleteTicket() {
            const willDelete = await window.swal({
                title: 'حذف تیکت',
                text: 'آیا از حذف کامل این تیکت اطمینان دارید؟ این عملیات برگشت‌پذیر نیست.',
                icon: 'warning',
                buttons: ['انصراف', 'بله، حذف شود'],
                dangerMode: true,
            });
            if (!willDelete) return;
            this.actionLoading = true;
            try {
                await this.$axios.delete(`admin/support_requests/${this.id}`);
                this.$root.$emit('toast', { message: 'تیکت با موفقیت حذف شد', variant: 'success' });
                this.$router.push(`/admin/tickets-list?type=${this.request.supportRequest.type}`);
            } catch (e) {
                this.$root.$emit('toast', { message: 'خطا در حذف تیکت', variant: 'danger' });
                this.actionLoading = false;
            }
        },
        scrollToBottom() {
            const el = this.$refs.chatBody;
            if (el) el.scrollTop = el.scrollHeight;
        },
        getInitials(customer) {
            if (!customer) return '?';
            return (customer.first_name || '?')[0];
        },
    },
};
</script>
<style scoped src="@@/core/pages/ticketManagement/supportTicketDetail.css"></style>
